import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Linkedin, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "@/assets/logo-optimized.webp";

const navLinks = [
  { label: "About", href: "/#about" },
  { label: "Case Studies", href: "/#case-studies" },
  // { label: "Tools", href: "/#tools" }, // Hidden for now
  { label: "Insights", href: "/#insights" },
  { label: "Contact", href: "/#contact" },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToHash = useCallback((hash: string) => {
    const targetId = hash.replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const hash = href.replace('/', '');

    if (location.pathname === '/') {
      // Already on homepage, just scroll
      scrollToHash(hash);
    } else {
      // Navigate to homepage first, then scroll after render
      navigate('/');
      setTimeout(() => scrollToHash(hash), 150);
    }
  }, [location.pathname, navigate, scrollToHash]);

  const handleMobileNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const hash = href.replace('/', '');

    const delay = location.pathname === '/' ? 100 : 150;
    if (location.pathname !== '/') {
      navigate('/');
    }
    setTimeout(() => scrollToHash(hash), delay);
  }, [location.pathname, navigate, scrollToHash]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50" role="banner">
      <div className="container-wide">
        <nav className="flex items-center justify-between h-16 md:h-20" aria-label="Main navigation">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md" aria-label="Digital Executive - Home">
            <img 
              src={logo} 
              alt="" 
              className="w-10 h-10 object-contain"
              aria-hidden="true"
              width={40}
              height={40}
              decoding="async"
            />
            <span className="font-semibold text-foreground">
              Digital Executive
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8" role="navigation">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium link-underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="https://www.linkedin.com/in/brianjkopp/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md p-1"
              aria-label="Connect on LinkedIn (opens in new tab)"
            >
              <Linkedin className="w-5 h-5" aria-hidden="true" />
            </a>
            <Button variant="hero" size="sm" asChild>
              <a href="/#contact" onClick={(e) => handleNavClick(e, '/#contact')}>
                <Mail className="w-4 h-4" aria-hidden="true" />
                Let's Connect
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-b border-border"
            role="navigation"
            aria-label="Mobile navigation"
          >
            <div className="container-wide py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-foreground font-medium py-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
                  onClick={(e) => handleMobileNavClick(e, link.href)}
                >
                  {link.label}
                </a>
              ))}
              <Button variant="hero" className="mt-2" asChild>
                <a href="/#contact" onClick={(e) => handleMobileNavClick(e, '/#contact')}>
                  <Mail className="w-4 h-4" aria-hidden="true" />
                  Let's Connect
                </a>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
